import type { EditorDraft } from "@rakazo/contracts";
import { EditorDraftSchema } from "@rakazo/contracts";
import { createContext, useContext, useRef, useState } from "react";

export const WorkspaceScopeContext = createContext<string | null>(null);
export const WorkspacePaneWidthContext = createContext<number | null>(null);
/**
 * Whether this window is the one in front. Only the foreground window may rewrite the shared
 * address; a background window tidying its own query must not pull the app onto its route.
 * Outside the windowed workspace there is one view, so it is always the foreground.
 */
export const WorkspaceWindowFocusContext = createContext(true);
/**
 * Which window a view belongs to. Unsaved-work guards and close handling are per window: closing
 * one note must not discard the warning protecting another. Null outside the windowed workspace.
 */
export const WorkspaceWindowIdContext = createContext<string | null>(null);
/** How a view moves its own window. Null outside the windowed workspace, where routing is global. */
export const WorkspaceWindowNavigateContext = createContext<((path: string) => void) | null>(null);
/** How a view names its own window. Null outside the windowed workspace. */
export const WorkspaceWindowTitleContext = createContext<
  ((id: string, title: string | null) => void) | null
>(null);
/** The desktop owns the global search shortcut; standalone screens keep their own. */
export const WorkspaceSearchHotkeyContext = createContext(true);

/** Disposable view preferences. Draft contents use the guarded recovery stores below. */
export function useWorkspaceView<T extends object>(
  resource: string,
  schema: { safeParse: (value: unknown) => { success: true; data: T } | { success: false } },
  initial: T,
) {
  const scope = useContext(WorkspaceScopeContext);
  const key = scope ? `andrew:view:${scope}:${resource}` : null;
  const [value, setValue] = useState<T>(() => {
    try {
      const parsed = schema.safeParse(
        JSON.parse(key ? (localStorage.getItem(key) ?? "null") : "null"),
      );
      return parsed.success ? parsed.data : initial;
    } catch {
      return initial;
    }
  });
  const current = useRef(value);
  function update(patch: Partial<T>) {
    const next = { ...current.current, ...patch };
    current.current = next;
    setValue(next);
    if (key)
      try {
        const parsed = schema.safeParse(next);
        if (parsed.success) localStorage.setItem(key, JSON.stringify(parsed.data));
      } catch {
        // A preference failure must not block browsing or discard unsaved work.
      }
  }
  return { value, current, update };
}
export function editorTabsKey(scope: string | null, windowId: string | null) {
  return scope ? `andrew:editor-tabs:${scope}${windowId ? `:${windowId}` : ""}` : null;
}

/** Retire only disposable tab chrome; document contents have their own recovery store. */
export function clearEditorTabs(scope: string | null, windowId: string) {
  const key = editorTabsKey(scope, windowId);
  if (key)
    try {
      localStorage.removeItem(key);
    } catch {
      /* Storage failures must not prevent opening or closing a window. */
    }
}

const editorMemory = new Map<string, EditorDraft>();
export function editorDraftKey(scope: string | null, resource: string) {
  return scope ? `andrew:editor:${scope}:${resource}` : null;
}
export function writeEditorDraft(key: string | null, draft: EditorDraft): boolean {
  if (!key) return false;
  try {
    EditorDraftSchema.parse(draft);
    localStorage.setItem(key, JSON.stringify(draft));
    editorMemory.delete(key);
    return true;
  } catch {
    editorMemory.set(key, draft);
    return false;
  }
}

export function readEditorDraft(key: string | null): EditorDraft | null {
  if (!key) return null;
  if (editorMemory.has(key)) return editorMemory.get(key)!;
  try {
    return EditorDraftSchema.parse(JSON.parse(localStorage.getItem(key) ?? "null"));
  } catch {
    return null;
  }
}

/** Drafts stay on this device and are scoped to the authenticated owner and space. */
export function useEditorDraft(resource: string) {
  const scope = useContext(WorkspaceScopeContext);
  const key = editorDraftKey(scope, resource);
  const [restored] = useState(() => readEditorDraft(key));
  const revision = useRef<string | null | undefined>(undefined);
  if (revision.current === undefined) {
    try {
      revision.current = key ? localStorage.getItem(key) : null;
    } catch {
      revision.current = null;
    }
  }
  const [draft, setDraft] = useState<EditorDraft>(
    () =>
      restored ?? {
        version: 1,
        name: "Untitled.md",
        text: "",
        saved: "",
        preview: false,
        selectionStart: 0,
        selectionEnd: 0,
        scrollTop: 0,
      },
  );
  const current = useRef(draft);
  const [durable, setDurable] = useState(() => !key || !editorMemory.has(key));
  function update(patch: Partial<EditorDraft>) {
    const next = { ...current.current, ...patch };
    current.current = next;
    setDraft(next);
    try {
      // Another window may have advanced this document since we opened it.
      // Keep this edit in memory and expose the existing unsaved-work warning;
      // never silently replace the other window's recovery copy.
      if (key && localStorage.getItem(key) !== revision.current) {
        editorMemory.set(key, next);
        setDurable(false);
        return;
      }
      const saved = writeEditorDraft(key, next);
      if (saved && key) revision.current = localStorage.getItem(key);
      setDurable(saved);
    } catch {
      if (key) editorMemory.set(key, next);
      setDurable(false);
    }
  }
  return { draft, update, restored: Boolean(restored), durable };
}
